//@flow
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import {
    validateRiderPersonaType,
    validateFleetType,
    RiderPersona as MasterRiderPersona,
    RiderPersonaTypeEnum
} from 'common/models/internal/MasterRiderPersona';
import { TransporterId } from 'common/models/internal/TransporterId';
import { EntityImportHelper } from 'common/entity/helpers/EntityImportHelper';
import * as Immutable from 'immutable';
import isNil from 'lodash/isNil';
import type { TableColumnTypes } from 'common/entity/generic/EntityImportContainer/types';
import DataUtils from 'common/utils/DataUtils';
import { RiderPersonaLimits } from 'common/models/internal/RiderPersonaLimits';
import { UnitEnum } from 'common/models/internal/RiderPersonaResourceLimit';

export class RiderPersonaImportHelper extends EntityImportDataHelper {
    static processTransporterId({ cellValue, clientId }) {
        return cellValue
            ? {
                  transporterId: cellValue.trim(),
                  clientId
              }
            : null;
    }

    static addUnitForTimeResourceLimit(
        limits: RiderPersonaLimits,
        resourceName: string
    ): RiderPersonaLimits {
        if (!DataUtils.isEmpty(limits.get(resourceName))) {
            return limits.setIn([resourceName, 'unit'], UnitEnum.SECONDS);
        }
        return limits;
    }

    static serializeTransporterId = (transporterId: {
        transporterId: string,
        clientId: string
    }): TransporterId => TransporterId.fromJS(transporterId);

    static validateType({ entity }) {
        const { type, enablePjp } = entity;
        if (!enablePjp) {
            return null;
        }
        return validateRiderPersonaType(type);
    }

    static validateFleetType({ entity }) {
        const { fleetType } = entity;
        return validateFleetType(fleetType);
    }

    static validateHigherThresh({ entity, header }) {
        const accessor = header?.accessor?.slice(0, 2);

        const resource = entity?.getIn(accessor);
        return resource?.validateHigherThresh?.() || null;
    }

    static validateVolumeUnit({
        entity,
        header
    }: {
        entity: MasterRiderPersona,
        header: TableColumnTypes
    }): ?string {
        const {
            limits: {
                volume: { unit }
            }
        } = entity;

        if (header.required || !isNil(unit)) {
            return EntityImportDataHelper.validateVolumeUnit(unit);
        }
        return null;
    }

    static getCustomFields(entitySettings) {
        return entitySettings?.riderPersonaSettings?.customFields;
    }

    static getDataForCell = ({ entitySettings }) => {
        return {
            entitySettings
        };
    };

    static processEntityRows = ({ rows }, { getDataForCell }) => {
        const { entitySettings } = getDataForCell();
        return rows.map(entityRow => {
            let updatedEntityRow = entityRow.map((cellValue, headerId) => {
                switch (headerId) {
                    case 'customProperties':
                        return cellValue.filter(value => value);
                    case 'limits': {
                        let modifiedValue = cellValue;

                        if (!cellValue.getIn(['task', 'name'])) {
                            modifiedValue = cellValue
                                .setIn(['task', 'name'], 'task')
                                .setIn(['location', 'name'], 'location')
                                .setIn(['distance', 'name'], 'distance')
                                .setIn(['travelTime', 'name'], 'travelTime')
                                .setIn(['totalTime', 'name'], 'totalTime')
                                .setIn(['volume', 'name'], 'volume')
                                .setIn(['weight', 'name'], 'weight');
                        }
                        // add weight unit
                        if (!DataUtils.isEmpty(modifiedValue.get('weight'))) {
                            modifiedValue = modifiedValue.setIn(
                                ['weight', 'unit'],
                                UnitEnum.KG
                            );
                        }

                        const modifiedCustomResourceLimits = EntityImportDataHelper.getResourceLimits(
                            cellValue.get('resourceLimits')
                        );

                        modifiedValue = modifiedValue.setIn(
                            ['resourceLimits'],
                            modifiedCustomResourceLimits
                        );

                        // add distance unit
                        if (!DataUtils.isEmpty(modifiedValue.get('distance'))) {
                            modifiedValue = modifiedValue.setIn(
                                ['distance', 'unit'],
                                UnitEnum.METERS
                            );
                        }
                        modifiedValue = RiderPersonaImportHelper.addUnitForTimeResourceLimit(
                            modifiedValue,
                            'travelTime'
                        );
                        modifiedValue = RiderPersonaImportHelper.addUnitForTimeResourceLimit(
                            modifiedValue,
                            'totalTime'
                        );
                        return modifiedValue;
                    }
                    default:
                        return cellValue;
                }
            });

            //When PJP is disabled, we need to populate rider persona type to DELIVERY by default,
            // set enablePjp and enableLotr to true and set allowMultiTrip to false
            if (
                RiderPersonaImportHelper.isFieldHidden({
                    entitySettings
                })
            ) {
                updatedEntityRow = updatedEntityRow
                    .set('type', RiderPersonaTypeEnum.DELIVERY)
                    .set('enableAuth', true)
                    .set('enableLotr', true)
                    .set('allowMultiTrip', false);
            }
            return MasterRiderPersona.fromJS(updatedEntityRow.toJS());
        });
    };

    static getUploadedEntities = ({
        uploadedTable,
        clientId,
        getDataForCell
    }) => {
        const riderPersonasData = EntityImportHelper.makeDataForEntities({
            table: uploadedTable,
            clientId,
            getDataForCell,
            processEntityRows: RiderPersonaImportHelper.processEntityRows
        });
        const riderPersonaEntities = Immutable.List(
            riderPersonasData.map(riderPersonaEntity =>
                MasterRiderPersona.fromJS(riderPersonaEntity.toJS())
            )
        );

        return riderPersonaEntities;
    };

    static generateEntityList = (
        riderPersonasData: Array<*>
    ): Immutable.List<MasterRiderPersona> =>
        Immutable.List(
            riderPersonasData.map(riderPersonaData =>
                MasterRiderPersona.fromJS(riderPersonaData)
            )
        );

    static isFieldHidden = ({ entitySettings }) => {
        return !entitySettings.pjpSettings.enabled;
    };
}

export default RiderPersonaImportHelper;
